import React from 'react';
import { Info, Sparkles } from 'lucide-react';

/**
 * Inline status banner surfaced on Page 2 (Size & Quantity) when the
 * requested dimensions fall outside the live XLSX pricing catalog.
 *
 * Two modes:
 *  - `requiresCustomQuote`: dimensions exceed the press envelope (52").
 *    The auto-quote is meaningless in this zone; we show an amber CTA
 *    that routes to the custom-quote handler.
 *  - `extrapolated`: dimensions are inside the envelope but outside the
 *    XLSX catalog grid. We still show the price but label it as an
 *    estimate so the customer knows it's formula-derived.
 *
 * Renders nothing when both flags are false — the component is a no-op
 * for standard in-catalog sizes.
 */
export const PriceEstimateBanner = ({
  extrapolated,
  requiresCustomQuote,
  onRequestCustomQuote,
}) => {
  if (requiresCustomQuote) {
    return (
      <div
        className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 mb-5 flex items-start gap-3"
        data-testid="price-custom-quote-banner"
      >
        <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-amber-900 text-sm">
            Dimensions exceed our auto-quote range
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Jobs larger than 52&quot; per side need pressroom review for an
            accurate quote. Click below and our team will respond with a
            firm price within 1 business day.
          </p>
          <button
            type="button"
            onClick={onRequestCustomQuote}
            data-testid="price-custom-quote-btn"
            className="mt-2 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-full px-4 py-1.5 transition-colors"
          >
            Request a Custom Quote →
          </button>
        </div>
      </div>
    );
  }

  if (extrapolated) {
    return (
      <div
        className="rounded-lg border border-[#4A7C59]/30 bg-[#4A7C59]/5 px-4 py-2.5 mb-5 flex items-center gap-3"
        data-testid="price-estimate-banner"
      >
        <Sparkles className="w-4 h-4 text-[#4A7C59] flex-shrink-0" />
        <p className="text-xs text-gray-700">
          <span className="font-semibold text-[#4A7C59]">Estimate</span>
          {' — '}
          This size is outside our standard catalog so the price is
          formula-derived. It&apos;s a close guide; for jobs over $500 we
          recommend confirming via a firm quote.
        </p>
      </div>
    );
  }

  return null;
};

export default PriceEstimateBanner;
